import { BookmarkActions } from 'actions/actionTypes';

import type {
  IBackgroundMarkerAttempt,
  IMarkerRequestMetadata,
  TMarkerEvaluationOutcome,
  TMarkerRequestConsumer,
  TMarkerRequestReason
} from './highlights';

export interface IBookmark extends Record<string, unknown> {
  uuid: string;
  meeting: string;
}

export interface IBookmarksState {
  data: Record<string, Record<string, IBookmark>>;
  requestsByMeeting: Record<string, IMarkerRequestMetadata>;
  entityRevisionByMeeting: Record<string, number>;
  latestEntityRequestIdByMeeting: Record<string, string>;
  lastBackgroundAttemptByHostInstance: Record<string, IBackgroundMarkerAttempt>;
}

interface IBookmarkAction {
  type: string;
  requestId?: string;
  meetingUuid?: string;
  meetingId?: string;
  reason?: TMarkerRequestReason;
  consumer?: TMarkerRequestConsumer;
  affectsSnippetsLoading?: boolean;
  entryToken?: string;
  hostInstanceId?: string;
  entityRevisionAtStart?: number;
  attempt?: number;
  evaluationOutcome?: TMarkerEvaluationOutcome;
  evaluationOnly?: boolean;
  releaseHost?: boolean;
  joined?: boolean;
  promoted?: boolean;
  revalidation?: boolean;
  countFollowup?: boolean;
  applyEntities?: boolean;
  superseded?: boolean;
  stale?: boolean;
  needsRevalidation?: boolean;
  error?: unknown;
  bookmark?: IBookmark;
  bookmarks?: IBookmark[];
  uuid?: string;
  meeting?: string;
}

export const bookmarksInitialState: IBookmarksState = {
  data: {},
  requestsByMeeting: {},
  entityRevisionByMeeting: {},
  latestEntityRequestIdByMeeting: {},
  lastBackgroundAttemptByHostInstance: {}
};

const getNextEntityRevision = (state: IBookmarksState, meetingUuid: string) =>
  (state.entityRevisionByMeeting?.[meetingUuid] || 0) + 1;

const getRequestAttempt = (action: IBookmarkAction): 1 | 2 =>
  action.attempt === 2 ? 2 : 1;

const getRequestReason = (action: IBookmarkAction): TMarkerRequestReason =>
  action.reason || 'legacy_recording_mount';

const recordBackgroundAttempt = (
  state: IBookmarksState,
  action: IBookmarkAction
) => {
  if (
    action.consumer !== 'background' ||
    !action.hostInstanceId ||
    !action.entryToken
  ) {
    return state.lastBackgroundAttemptByHostInstance || {};
  }

  return {
    ...(state.lastBackgroundAttemptByHostInstance || {}),
    [action.hostInstanceId]: {
      meetingUuid: action.meetingUuid || '',
      entryToken: action.entryToken,
      evaluationOutcome:
        action.evaluationOutcome || (action.joined ? 'joined' : 'started')
    }
  };
};

const createRequestMetadata = (
  state: IBookmarksState,
  action: IBookmarkAction,
  previous?: IMarkerRequestMetadata
): IMarkerRequestMetadata => ({
  requestId: action.requestId || previous?.requestId || '',
  meetingUuid: action.meetingUuid || previous?.meetingUuid || '',
  status: 'loading',
  error: null,
  reason: action.reason || previous?.reason || getRequestReason(action),
  consumer:
    action.consumer ||
    (action.promoted ? 'foreground' : previous?.consumer) ||
    'background',
  affectsSnippetsLoading:
    previous?.affectsSnippetsLoading === true ||
    action.affectsSnippetsLoading === true,
  entryToken: action.entryToken || previous?.entryToken,
  hostInstanceId: action.hostInstanceId || previous?.hostInstanceId,
  entityRevisionAtStart:
    action.entityRevisionAtStart ??
    previous?.entityRevisionAtStart ??
    (action.meetingUuid
      ? state.entityRevisionByMeeting?.[action.meetingUuid] || 0
      : 0),
  attempt: getRequestAttempt(action),
  evaluationOutcome: action.evaluationOutcome || previous?.evaluationOutcome,
  joined: previous?.joined === true || action.joined === true,
  promoted: previous?.promoted === true || action.promoted === true,
  revalidation: action.revalidation === true,
  countFollowup: action.countFollowup === true,
  superseded: false,
  needsRevalidation: action.revalidation
    ? previous?.needsRevalidation === true
    : false,
  lastSucceededAt: previous?.lastSucceededAt
});

const toBookmarkMap = (bookmarks: IBookmark[]) =>
  bookmarks.reduce<Record<string, IBookmark>>((bookmarkMap, bookmark) => {
    bookmarkMap[bookmark.uuid] = bookmark;
    return bookmarkMap;
  }, {});

const bookmarks = (
  state: IBookmarksState = bookmarksInitialState,
  action: IBookmarkAction = { type: '@@INIT' }
): IBookmarksState => {
  switch (action.type) {
    case BookmarkActions.CREATE_BOOKMARK_SUCCESS:
    case BookmarkActions.FETCH_BOOKMARK_SUCCESS: {
      if (!action.bookmark) return state;
      const meetingUuid = action.bookmark.meeting;
      return {
        ...state,
        data: {
          ...state.data,
          [meetingUuid]: {
            ...state.data[meetingUuid],
            [action.bookmark.uuid]: {
              ...action.bookmark
            }
          }
        },
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [meetingUuid]: getNextEntityRevision(state, meetingUuid)
        }
      };
    }
    case BookmarkActions.FETCH_BOOKMARKS_REQUEST: {
      if (
        action.evaluationOnly &&
        action.releaseHost &&
        action.hostInstanceId
      ) {
        const lastBackgroundAttemptByHostInstance = {
          ...(state.lastBackgroundAttemptByHostInstance || {})
        };
        delete lastBackgroundAttemptByHostInstance[action.hostInstanceId];
        return {
          ...state,
          lastBackgroundAttemptByHostInstance
        };
      }

      if (!action.requestId || !action.meetingUuid) return state;

      const lastBackgroundAttemptByHostInstance = recordBackgroundAttempt(
        state,
        action
      );
      if (action.evaluationOnly) {
        return {
          ...state,
          lastBackgroundAttemptByHostInstance
        };
      }

      const previousRequest = state.requestsByMeeting?.[action.meetingUuid];
      const isSameLogicalRequest =
        previousRequest?.requestId === action.requestId;
      const request = createRequestMetadata(
        state,
        action,
        isSameLogicalRequest ? previousRequest : undefined
      );

      return {
        ...state,
        requestsByMeeting: {
          ...state.requestsByMeeting,
          [action.meetingUuid]: request
        },
        latestEntityRequestIdByMeeting: {
          ...state.latestEntityRequestIdByMeeting,
          [action.meetingUuid]: action.requestId
        },
        lastBackgroundAttemptByHostInstance
      };
    }
    case BookmarkActions.FETCH_BOOKMARKS_SUCCESS: {
      if (!action.requestId || !action.meetingUuid) {
        if (!action.meetingUuid) return state;
        return {
          ...state,
          data: {
            ...state.data,
            [action.meetingUuid]: toBookmarkMap(action.bookmarks || [])
          }
        };
      }

      const request = state.requestsByMeeting?.[action.meetingUuid];
      if (!request || request.requestId !== action.requestId) return state;

      const latestRequestMatches =
        state.latestEntityRequestIdByMeeting?.[action.meetingUuid] ===
        action.requestId;
      const currentRevision =
        state.entityRevisionByMeeting?.[action.meetingUuid] || 0;
      const responseStartRevision =
        action.entityRevisionAtStart ?? request.entityRevisionAtStart;
      const revisionMatches = currentRevision === responseStartRevision;
      const superseded = action.superseded === true || !latestRequestMatches;
      const stale = !superseded && (action.stale === true || !revisionMatches);
      const shouldApplyEntities =
        action.applyEntities !== false && !superseded && !stale;
      const terminalRequest: IMarkerRequestMetadata = {
        ...request,
        status: stale ? 'stale' : 'succeeded',
        error: null,
        attempt: getRequestAttempt(action),
        superseded,
        needsRevalidation: stale || action.needsRevalidation === true,
        lastSucceededAt: stale ? request.lastSucceededAt : Date.now()
      };

      return {
        ...state,
        data: shouldApplyEntities
          ? {
              ...state.data,
              [action.meetingUuid]: toBookmarkMap(action.bookmarks || [])
            }
          : state.data,
        requestsByMeeting: {
          ...state.requestsByMeeting,
          [action.meetingUuid]: terminalRequest
        }
      };
    }
    case BookmarkActions.FETCH_BOOKMARKS_FAILED: {
      if (!action.requestId || !action.meetingUuid) return state;
      const request = state.requestsByMeeting?.[action.meetingUuid];
      if (!request || request.requestId !== action.requestId) return state;

      return {
        ...state,
        requestsByMeeting: {
          ...state.requestsByMeeting,
          [action.meetingUuid]: {
            ...request,
            status: 'failed',
            error: action.error,
            attempt: getRequestAttempt(action),
            superseded: action.superseded === true,
            needsRevalidation: action.needsRevalidation === true
          }
        }
      };
    }
    case BookmarkActions.UDPATE_BOOKMARK_SUCCESS: {
      if (!action.meeting) return state;
      return {
        ...state,
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [action.meeting]: getNextEntityRevision(state, action.meeting)
        }
      };
    }
    case BookmarkActions.DELETE_BOOKMARK_SUCCESS: {
      if (!action.meetingId || !action.uuid) return state;
      const meetingBookmarks = state.data[action.meetingId] || {};
      const remainingMeetingBookmarks = { ...meetingBookmarks };
      delete remainingMeetingBookmarks[action.uuid];
      return {
        ...state,
        data: {
          ...state.data,
          [action.meetingId]: remainingMeetingBookmarks
        },
        entityRevisionByMeeting: {
          ...state.entityRevisionByMeeting,
          [action.meetingId]: getNextEntityRevision(state, action.meetingId)
        }
      };
    }
    default:
      return state;
  }
};

export default bookmarks;
